import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import Header from "../../../Common/Layout/Header";
import { useFlightSearch } from "../../hooks/useFlightSearch";
import styles from "./Flights6.module.css";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiFilterLine,
  RiTimeLine,
  RiMoneyDollarCircleLine,
  RiCheckLine,
  RiCloseLine,
  RiArrowDownSLine,
  RiSuitcaseLine,
  RiRefreshLine,
  RiCalendar2Line,
  RiUserLine,
  RiPlaneLine,
  RiInformationLine,
  RiAlertLine,
  RiMapPinLine
} from "react-icons/ri";

/* ─── Utility ─────────────────────────────────────────────────────────── */
const formatTime = (dt) => {
  if (!dt) return "--:--";
  try { return moment(dt).format("HH:mm"); } catch { return "--:--"; }
};

const formatDuration = (mins) => {
  if (!mins && mins !== 0) return "N/A";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m > 0 ? m + "m" : ""}`.trim();
};

const formatPrice = (val) => {
  if (!val && val !== 0) return "0";
  return Number(val).toLocaleString("en-IN");
};

const getMinPrice = (flightLeg) => {
  if (!flightLeg?.dcPriceDetails?.length) return 0;
  return Math.min(...flightLeg.dcPriceDetails.map((fare) => fare.totalPrice || Infinity));
};

const Flights6 = ({ mode, onNavigate, templateId, params: builderParams, styleConfig, companyId }) => {
  const urlParams = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const finalCompanyId = (mode === "builder" || mode === "preview")
    ? (user?.isSupplierAPI === true ? user?.cid : user?.parentCompanyId)
    : companyId;

  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#7c5cbf",
    "--secondary-color": styleConfig?.secondary || "#e879a0",
  };

  const params = builderParams || urlParams;
  const { adults = 1, kids = 0, infants = 0, isDomestic = "DOMESTIC" } = params;
  
  const actualSearchQuery = mode === "builder" ? builderParams?.searchQuery : location.state?.searchQuery || {};
  
  const [searchQuery, setSearchQuery] = useState({
      ...actualSearchQuery,
      companyId: finalCompanyId || actualSearchQuery.companyId
  });

  useEffect(() => {
    if (!actualSearchQuery || Object.keys(actualSearchQuery).length === 0) {
      if (mode === "live") navigate("/template/flight/search");
    } else {
      setSearchQuery({
          ...actualSearchQuery,
          companyId: finalCompanyId || actualSearchQuery.companyId
      });
    }
  }, [actualSearchQuery, mode, navigate, finalCompanyId]);

  const journeyType = searchQuery?.journeyType || "ONEWAY";
  const isRoundTrip = journeyType === "RETURN";

  /* ── State ── */
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFares, setSelectedFares] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [expandTab, setExpandTab] = useState("fares");
  const [selectedFareIdxMap, setSelectedFareIdxMap] = useState({});
  const [selectedFaresRoundTrip, setSelectedFaresRoundTrip] = useState({});

  const { data: flightData = [], isLoading: pageLoading } = useFlightSearch(searchQuery);

  /* ── Filters ── */
  const [filterStops, setFilterStops] = useState([]);
  const [filterAirlines, setFilterAirlines] = useState([]);
  const [filterMaxPrice, setFilterMaxPrice] = useState(null);
  const [filterTimeSlots, setFilterTimeSlots] = useState([]);
  const [sortBy, setSortBy] = useState("price");
  const [page, setPage] = useState(1);
  const PER_PAGE = 8;
  const currencySymbol = "₹"; 

  const activeFlights = useMemo(() => {
    return flightData[activeTab]?.dcFlightLegs || [];
  }, [flightData, activeTab]);

  const airlines = useMemo(() => {
    const map = {};
    activeFlights.forEach((f) => {
      const name = f.dcDisplayLegs?.[0]?.airlineData?.airline || "Unknown";
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map).map(([name, count]) => ({ name, count }));
  }, [activeFlights]);

  const maxPrice = useMemo(() => {
    if (!activeFlights.length) return 50000;
    return Math.max(...activeFlights.map((f) => getMinPrice(f)));
  }, [activeFlights]);

  useEffect(() => {
    if (maxPrice > 0 && filterMaxPrice === null) setFilterMaxPrice(maxPrice);
  }, [maxPrice, filterMaxPrice]);

  const filtered = useMemo(() => {
    let list = [...activeFlights];
    if (filterStops.length) {
      list = list.filter((f) => filterStops.includes(f?.dcDisplayLegs?.[0]?.stops ?? 0));
    }
    if (filterAirlines.length) {
      list = list.filter((f) => filterAirlines.includes(f?.dcDisplayLegs?.[0]?.airlineData?.airline));
    }
    if (filterMaxPrice !== null) {
      list = list.filter((f) => getMinPrice(f) <= filterMaxPrice);
    }
    if (filterTimeSlots.length) {
      list = list.filter((f) => {
        const dt = f?.dcDisplayLegs?.[0]?.startSegment?.departureDateTime;
        if (!dt) return false;
        const h = new Date(dt).getHours();
        return filterTimeSlots.some((slot) => {
          if (slot === "early") return h >= 0 && h < 6;
          if (slot === "morn") return h >= 6 && h < 12;
          if (slot === "after") return h >= 12 && h < 18;
          if (slot === "eve") return h >= 18;
          return false;
        });
      });
    }

    list.sort((a, b) => {
      if (sortBy === "price") return getMinPrice(a) - getMinPrice(b);
      if (sortBy === "duration") return (a?.dcDisplayLegs?.[0]?.totalDuration || 0) - (b?.dcDisplayLegs?.[0]?.totalDuration || 0);
      return 0;
    });
    return list;
  }, [activeFlights, filterStops, filterAirlines, filterMaxPrice, filterTimeSlots, sortBy]);

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const toggleExpand = (id) => {
    setExpandedId((p) => (p === id ? null : id));
    setExpandTab("fares");
  };

  const routeToDetails = (payload, passedJourneyType) => {
    if (mode === "builder") {
      onNavigate({ name: "flight-details", params: { searchQuery, payload, journeyType: passedJourneyType } });
    } else {
      navigate(`/template/flight-details/${adults}/${kids}/${infants}/${isDomestic}`, {
        state: { searchQuery, payload, journeyType: passedJourneyType },
      });
    }
  };

  const handleBookFlight = (flightLeg, e) => {
    e?.stopPropagation();
    const isOutbound = activeTab === 0;
    const currentJourneyType = flightData[activeTab]?.journeyType || "ONEWAY";
    const selectedFareIdx = selectedFareIdxMap[flightLeg.supplierLegId] ?? 0;
    const cheapestFare = flightLeg.dcPriceDetails?.[selectedFareIdx] || [...flightLeg.dcPriceDetails].sort((a, b) => a.totalPrice - b.totalPrice)[0];

    if (!isRoundTrip) {
      routeToDetails([{ flightLeg, fare: cheapestFare, journeyType: currentJourneyType }], currentJourneyType);
    } else {
      const routeKey = isOutbound ? "outbound" : "return";
      setSelectedFaresRoundTrip((prev) => ({
        ...prev,
        [routeKey]: { flightLeg, fare: cheapestFare, journeyType: currentJourneyType, locationFrom: flightLeg?.dcDisplayLegs?.[0]?.startSegment?.airportCode, locationTo: flightLeg?.dcDisplayLegs?.[0]?.endSegment?.airportCode }
      }));
      if (flightData.length > 1) setActiveTab(isOutbound ? 1 : 0);
    }
  };

  const origin = searchQuery?.flightTravelerPoints?.[0]?.from || "—";
  const destination = searchQuery?.flightTravelerPoints?.[0]?.to || "—";
  const depDate = searchQuery?.flightTravelerPoints?.[0]?.departureDate || "";
  const totalPax = Number(adults) + Number(kids) + Number(infants);

  if (authLoading) return <div className={styles.pageLoader}>Aurora is finding your flights...</div>;

  return (
    <div className={styles.page} style={themeVariables}>
      {/* ── STICKY TOP BAR ── */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.routeBox}>
            <RiMapPinLine className={styles.routeIcon} />
            <span className={styles.cityName}>{origin}</span>
            <RiArrowRightLine className={styles.routeArrow} />
            <span className={styles.cityName}>{destination}</span>
          </div>

          <div className={styles.metaRow}>
            <span className={styles.metaPill}><RiCalendar2Line /> {moment(depDate).format("DD MMM YY")}</span>
            <span className={styles.metaPill}><RiUserLine /> {totalPax} Traveller{totalPax !== 1 ? "s" : ""}</span>
            <span className={styles.metaPill}><RiPlaneLine /> {searchQuery?.cabinClass || "ECONOMY"}</span>
          </div>

          <button className={styles.modifyBtn} onClick={() => navigate(-1)}><RiFilterLine /> Modify</button>
        </div>
      </div>

      <div className={styles.main}>
        {/* ── SIDEBAR ── */}
        <aside className={styles.sidebar}>
          <div className={styles.filterCard}>
            <div className={styles.groupTitle}>Price Range</div>
            <div className={styles.priceMeta}>
              <span>{currencySymbol}0</span>
              <span className={styles.accentText}>{currencySymbol}{formatPrice(filterMaxPrice)}</span>
            </div>
            <input type="range" className={styles.rangeSlider} min={0} max={maxPrice} value={filterMaxPrice ?? maxPrice} onChange={(e) => setFilterMaxPrice(Number(e.target.value))} />
          </div>

          <div className={styles.filterCard}>
            <div className={styles.groupTitle}>Stops</div>
            {[{v:0, l:"Non-Stop"}, {v:1, l:"1 Stop"}, {v:2, l:"2+ Stops"}].map(s => (
              <label key={s.v} className={styles.checkItem}>
                <input type="checkbox" checked={filterStops.includes(s.v)} onChange={() => setFilterStops(p => p.includes(s.v) ? p.filter(x => x!==s.v) : [...p, s.v])} />
                <span className={styles.customCheck}><RiCheckLine /></span> {s.l}
              </label>
            ))}
          </div>

          <div className={styles.filterCard}>
            <div className={styles.groupTitle}>Airlines</div>
            {airlines.map(a => (
              <label key={a.name} className={styles.checkItem}>
                <input type="checkbox" checked={filterAirlines.includes(a.name)} onChange={() => setFilterAirlines(p => p.includes(a.name) ? p.filter(x => x!==a.name) : [...p, a.name])} />
                <span className={styles.customCheck}><RiCheckLine /></span> {a.name}
              </label>
            ))}
          </div>
          
          <button className={styles.clearBtn} onClick={() => {setFilterStops([]); setFilterAirlines([]); setFilterMaxPrice(maxPrice);}}>
            <RiRefreshLine /> Clear All Filters
          </button>
        </aside>

        {/* ── RESULTS ── */}
        <div className={styles.resultsPanel}>
          <div className={styles.resultsHeader}>
            <div className={styles.countText}>Showing <span className={styles.accentText}>{filtered.length} Flights</span> available</div>
            
            {isRoundTrip && flightData.length > 1 && (
              <div className={styles.tabSwitcher}>
                {flightData.map((item, idx) => (
                  <button key={idx} className={`${styles.tabBtn} ${activeTab === idx ? styles.tabBtnActive : ""}`} onClick={() => setActiveTab(idx)}>
                    {idx === 0 ? "Outbound" : "Return"}
                  </button>
                ))}
              </div>
            )}

            <div className={styles.sortBox}>
              <span className={styles.sortLabel}>Sort by:</span>
              <button className={`${styles.sortChip} ${sortBy === 'price' ? styles.sortChipActive : ''}`} onClick={() => setSortBy('price')}>Cheapest</button>
              <button className={`${styles.sortChip} ${sortBy === 'duration' ? styles.sortChipActive : ''}`} onClick={() => setSortBy('duration')}>Fastest</button>
            </div>
          </div>

          {pageLoading ? (
            <div className={styles.loaderWrap}><div className={styles.spinner}></div><p>Gathering routes...</p></div>
          ) : (
            paginated.map((flightLeg, idx) => {
              const displayLeg = flightLeg?.dcDisplayLegs?.[0];
              const priceDetails = flightLeg?.dcPriceDetails || [];
              const selectedFareIdx = selectedFareIdxMap[flightLeg.supplierLegId] ?? 0;
              const fare = priceDetails[selectedFareIdx] || priceDetails[0];
              const isExpanded = expandedId === flightLeg.supplierLegId;
              const isSelected = isRoundTrip ? (activeTab === 0 ? selectedFaresRoundTrip.outbound?.flightLeg?.supplierLegId === flightLeg.supplierLegId : selectedFaresRoundTrip.return?.flightLeg?.supplierLegId === flightLeg.supplierLegId) : false;

              return (
                <div key={flightLeg.supplierLegId || idx} className={`${styles.flightCard} ${isSelected ? styles.cardSelected : ''}`}>
                  {isSelected && <div className={styles.selectedTag}>Selected Flight</div>}
                  
                  <div className={styles.cardMain} onClick={() => toggleExpand(flightLeg.supplierLegId)}>
                    <div className={styles.airlineSection}>
                      <div className={styles.logoContainer}>
                        <img src={`/assets/images/airlinelogos/${displayLeg?.airlineData?.airlineCode}.png`} alt="Logo" onError={(e) => {e.target.style.display="none"}} />
                        {!displayLeg?.airlineData?.airlineCode && <span>FL</span>}
                      </div>
                      <div className={styles.airlineMeta}>
                        <div className={styles.airlineName}>{displayLeg?.airlineData?.airline}</div>
                        <div className={styles.flightCode}>{displayLeg?.airlineData?.flightNumber}</div>
                      </div>
                    </div>

                    <div className={styles.itinerarySection}>
                      <div className={styles.timePoint}>
                        <div className={styles.timeVal}>{formatTime(displayLeg?.startSegment?.departureDateTime)}</div>
                        <div className={styles.portCode}>{displayLeg?.startSegment?.airportCode}</div>
                      </div>
                      <div className={styles.pathVisual}>
                        <div className={styles.durationText}>{formatDuration(displayLeg?.totalDuration)}</div>
                        <div className={styles.trackLine}>
                          <div className={styles.trackDot}></div>
                          <div className={styles.lineFill}><RiPlaneLine className={styles.planeIcon} /></div>
                          <div className={styles.trackDot}></div>
                        </div>
                        <div className={styles.stopStatus}>{displayLeg?.stops === 0 ? 'Non-stop' : `${displayLeg.stops} Stop`}</div>
                      </div>
                      <div className={styles.timePoint}>
                        <div className={styles.timeVal}>{formatTime(displayLeg?.endSegment?.arrivalDateTime)}</div>
                        <div className={styles.portCode}>{displayLeg?.endSegment?.airportCode}</div>
                      </div>
                    </div>

                    <div className={styles.priceSection}>
                      <div className={styles.priceVal}>{currencySymbol}{formatPrice(fare?.totalPrice)}</div>
                      <div className={styles.fareTag}>{fare?.fareIdentifier || "PUBLISHED"}</div>
                      <button className={styles.bookBtn} onClick={(e) => handleBookFlight(flightLeg, e)}>
                        {isRoundTrip ? (isSelected ? 'Selected' : 'Select') : 'Book Now'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className={styles.expandArea}>
                       <div className={styles.expandTabHeader}>
                          <button className={`${styles.exPill} ${expandTab === 'fares' ? styles.exPillActive : ''}`} onClick={() => setExpandTab('fares')}>Fare Options</button>
                          <button className={`${styles.exPill} ${expandTab === 'baggage' ? styles.exPillActive : ''}`} onClick={() => setExpandTab('baggage')}>Baggage Info</button>
                          <button className={`${styles.exPill} ${expandTab === 'itinerary' ? styles.exPillActive : ''}`} onClick={() => setExpandTab('itinerary')}>Itinerary</button>
                       </div>

                       {expandTab === 'fares' && (
                         <div className={styles.fareGrid}>
                            {priceDetails.map((f, i) => (
                              <div key={i} className={`${styles.fareCard} ${selectedFareIdx === i ? styles.fareCardActive : ""}`} onClick={() => setSelectedFareIdxMap({...selectedFareIdxMap, [flightLeg.supplierLegId]: i})}>
                                <div className={styles.fareTitle}>{f.fareIdentifier}</div>
                                <div className={styles.fareAmt}>{currencySymbol}{formatPrice(f.totalPrice)}</div>
                                <div className={styles.featureList}>
                                  <div className={styles.featItem}><RiSuitcaseLine /> {f.checkinBag || '20 Kg'} Check-in</div>
                                  <div className={styles.featItem}><RiSuitcaseLine /> {f.cabinBaggage || '7 Kg'} Cabin</div>
                                  <div className={styles.featItem}>
                                    {f.dcAdults?.mealType === "Free Meal" ? <RiCheckLine className={styles.colorOk}/> : <RiCloseLine className={styles.colorNo}/>} Meal Included
                                  </div>
                                  <div className={styles.featItem}>
                                    {f.dcAdults?.refundable === "Refundable" ? <RiCheckLine className={styles.colorOk}/> : <RiCloseLine className={styles.colorNo}/>} Refundable
                                  </div>
                                </div>
                              </div>
                            ))}
                         </div>
                       )}

                       {expandTab === 'baggage' && (
                        <div className={styles.baggageFlex}>
                          <div className={styles.bagBox}>
                            <div className={styles.bagIcon}>🧳</div>
                            <div className={styles.bagData}>
                              <strong>Check-in Baggage</strong>
                              <span>{fare?.checkinBag || "20 Kg"}</span>
                            </div>
                          </div>
                          <div className={styles.bagBox}>
                            <div className={styles.bagIcon}>🎒</div>
                            <div className={styles.bagData}>
                              <strong>Cabin Baggage</strong>
                              <span>{fare?.cabinBaggage || "7 Kg"}</span>
                            </div>
                          </div>
                          <div className={styles.bagBox}>
                            <div className={styles.bagIcon}>🍽️</div>
                            <div className={styles.bagData}>
                              <strong>Meal Service</strong>
                              <span>{fare?.dcAdults?.mealType === "Free Meal" ? "Complimentary" : "On Purchase"}</span>
                            </div>
                          </div>
                        </div>
                       )}

                       {expandTab === 'itinerary' && (
                         <div className={styles.itineraryList}>
                            {flightLeg.dcSegments?.map((seg, sIdx) => (
                              <React.Fragment key={sIdx}>
                               <div className={styles.segmentCard}>
                                  <div className={styles.segTop}>
                                    <img src={`/assets/images/airlinelogos/${seg?.tFAirlineData?.airlineCode}.png`} alt="Logo" width="24"/>
                                    <span>{seg?.tFAirlineData?.airline} · {seg?.tFAirlineData?.flightNumber}</span>
                                  </div>
                                  <div className={styles.segDetails}>
                                    <div className={styles.segBlock}>
                                      <strong>{formatTime(seg.tFDepartureData.departureDateTime)}</strong>
                                      <span>{seg.tFDepartureData.airportCode}</span>
                                    </div>
                                    <div className={styles.segDivider}>
                                      <span>{formatDuration(seg.tFAirlineData.duration)}</span>
                                      <div className={styles.lineDot}></div>
                                    </div>
                                    <div className={styles.segBlock}>
                                      <strong>{formatTime(seg.tFArrivalData.arrivalDateTime)}</strong>
                                      <span>{seg.tFArrivalData.airportCode}</span>
                                    </div>
                                  </div>
                               </div>
                               {sIdx < flightLeg.dcSegments.length - 1 && (
                                <div className={styles.layoverStrip}>
                                  <RiTimeLine /> Layover in {flightLeg.dcSegments[sIdx]?.tFArrivalData?.city}: {formatDuration(flightLeg.dcSegments[sIdx + 1]?.tFAirlineData?.connectingTime)}
                                </div>
                               )}
                              </React.Fragment>
                            ))}
                         </div>
                       )}
                    </div>
                  )}

                  <div className={styles.cardFooter} onClick={(e) => { e.stopPropagation(); toggleExpand(flightLeg.supplierLegId); }}>
                    <span>{isExpanded ? "Hide Details" : "Show Fare & Details"}</span>
                    <RiArrowDownSLine className={isExpanded ? styles.rotate180 : ""} />
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Flights6;